import { useMutation } from "@tanstack/react-query";
import log from "logbeacon";
import { CreatePositionOrderRequest } from "@/src/features/api/memberApi";
import { useOspApiClientProvider } from "@/src/hooks";

interface UseCreatePositionOrderOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * 创建订单的 Hook
 *
 * @param options 选项配置
 * @returns mutation 对象
 */
export const useCreatePositionOrder = (
  options?: UseCreatePositionOrderOptions,
) => {
  const { onSuccess } = options || {};
  const { memberApi } = useOspApiClientProvider();

  return useMutation({
    mutationFn: async ({
      accountId,
      data,
    }: {
      accountId: string;
      data: CreatePositionOrderRequest;
    }) => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }

      const response = await memberApi.positions.createPositionOrder(
        accountId,
        data,
      );
      return response;
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};

interface UseUpdatePositionOrderOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * 更新订单的 Hook
 *
 * @param options 选项配置
 * @returns mutation 对象
 */
export const useUpdatePositionOrder = (
  options?: UseUpdatePositionOrderOptions,
) => {
  const { onSuccess } = options || {};
  const { memberApi } = useOspApiClientProvider();

  return useMutation({
    mutationFn: async ({
      accountId,
      orderId,
      data,
    }: {
      accountId: string;
      orderId: string;
      data: CreatePositionOrderRequest;
    }) => {
      log.debug("[文案]Updating position order:", { accountId, orderId, data });

      if (!memberApi) {
        throw new Error("memberApi is not available");
      }

      const response = await memberApi.positions.updatePositionOrder(
        accountId,
        orderId,
        data,
      );
      return response;
    },
    onSuccess: async () => {
      onSuccess?.();
    },
  });
};

interface UseDeletePositionOrderOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * 删除订单的 Hook
 *
 * @param options 选项配置
 * @returns mutation 对象
 */
export const useDeletePositionOrder = (
  options?: UseDeletePositionOrderOptions,
) => {
  const { onSuccess } = options || {};
  const { memberApi } = useOspApiClientProvider();

  return useMutation({
    mutationFn: async ({
      accountId,
      orderId,
    }: {
      accountId: string;
      orderId: string;
    }) => {
      log.debug("[文案]Deleting position order:", { accountId, orderId });

      if (!memberApi) {
        throw new Error("memberApi is not available");
      }

      const response = await memberApi.positions.deletePositionOrder(
        accountId,
        orderId,
      );
      return response;
    },
    onSuccess: async () => {
      onSuccess?.();
    },
  });
};
